import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Divider,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Email,
  AccessTime,
  Menu as MenuIcon,
  Login,
  Phone,
  Receipt,
  WaterDrop,
  ArrowForward,
  Info,
  CheckCircle,
  AccountBalance,
  QrCode2,
  ShieldOutlined,
  Description,
  Business,
  Badge,
  Gavel,
  Assessment,
  LocalAtm,
  Domain,
  VerifiedUser,
  LocationOn,
  Security,
  OpenInNew
} from '@mui/icons-material';
import MobileBottomBar from './common/MobileBottomBar';

const Homepage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Philippine Standard Time Live Clock
  const [pstTime, setPstTime] = useState('');
  
  // Citizen's Charter Active Tab
  const [charterTab, setCharterTab] = useState(0);

  // Info Modals for Services
  const [serviceModal, setServiceModal] = useState(null); // 'rpt', 'bplo', 'mcr', 'cedula'

  useEffect(() => {
    document.title = "Municipal Treasurer's Office — LGU Concepcion, Romblon | Official Portal";
    
    const updatePST = () => {
      const now = new Date();
      const options = {
        timeZone: 'Asia/Manila',
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      };
      setPstTime(now.toLocaleString('en-US', options) + ' PST');
    };

    updatePST();
    const timer = setInterval(updatePST, 1000);

    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      clearInterval(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'left', height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#ffffff' }}>
      <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            component="img"
            src="/logo-left.png"
            alt="LGU Concepcion Seal"
            sx={{ width: 40, height: 40, objectFit: 'contain' }}
          />
          <Box
            component="img"
            src="/logo-right.png"
            alt="MTO Seal"
            sx={{ width: 40, height: 40, objectFit: 'contain' }}
          />
        </Box>
        <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
          Municipal Treasurer's Office
        </Typography>
        <Typography variant="caption" color="text.secondary">
          LGU Concepcion, Romblon
        </Typography>
      </Box>
      <List sx={{ px: 1.5, py: 2, flexGrow: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/check-water-bills')} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#334155' }}>
              <WaterDrop fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Water Billing & QRPh" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/check-unclaimed-checks')} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#334155' }}>
              <Receipt fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Unclaimed Checks" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => scrollToSection('services-section')} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#334155' }}>
              <Domain fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Municipal E-Services" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => scrollToSection('charter-section')} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#334155' }}>
              <Description fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Citizen's Charter" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => scrollToSection('transparency-section')} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#334155' }}>
              <ShieldOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Transparency & ARTA" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => scrollToSection('directory-section')} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#334155' }}>
              <LocationOn fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Helpdesk & Directory" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }} />
          </ListItemButton>
        </ListItem>
      </List>
      <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
        <Button 
          fullWidth
          variant="contained"
          startIcon={<Security />}
          onClick={() => navigate('/login')}
          sx={{ 
            bgcolor: '#1e293b', 
            color: '#ffffff',
            fontWeight: 700,
            py: 1,
            borderRadius: 1.5,
            textTransform: 'none',
            '&:hover': { bgcolor: '#0f172a' }
          }}
        >
          Staff Portal Login
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8fafc', color: '#0f172a' }}>
      
      {/* 1. Official Philippine Government Top Masthead (Clean Light Style) */}
      <Box sx={{ bgcolor: '#ffffff', color: '#334155', py: 0.75, px: 2, borderBottom: '1px solid #e2e8f0', fontSize: '0.78rem' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {/* Left: GOVPH & PST */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    bgcolor: '#1e293b',
                    color: '#ffffff',
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 0.5,
                    fontSize: '0.7rem'
                  }}
                >
                  GOVPH
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: { xs: 'none', sm: 'inline' } }}>
                  Republic of the Philippines
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 14, my: 'auto', display: { xs: 'none', sm: 'block' } }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <AccessTime sx={{ fontSize: 13, color: '#64748b' }} />
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569', fontFamily: 'monospace' }}>
                  {pstTime || 'Philippine Standard Time'}
                </Typography>
              </Box>
            </Box>

            {/* Right: Contact & ARTA Notice */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: 13, color: '#64748b' }} />
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
                  mtoconcepcion@gmail.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Phone sx={{ fontSize: 13, color: '#64748b' }} />
                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
                  PACD Hotline: (042) 123-4567
                </Typography>
              </Box>
              <Chip
                label="No Noon Break"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  bgcolor: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1'
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 2. Official Dual-Seal Government Header */}
      <Box sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0', py: { xs: 1.5, md: 2.2 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            {/* Dual Seals + Formal Department Typography */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
              <Box
                component="img"
                src="/logo-left.png"
                alt="Official Seal of Concepcion, Romblon"
                sx={{
                  width: { xs: 48, sm: 60, md: 64 },
                  height: { xs: 48, sm: 60, md: 64 },
                  objectFit: 'contain'
                }}
              />

              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#64748b', 
                    fontWeight: 700, 
                    letterSpacing: 0.8, 
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.65rem', sm: '0.72rem' },
                    display: 'block'
                  }}
                >
                  Republic of the Philippines • Province of Romblon
                </Typography>
                <Typography 
                  variant="h5" 
                  component="h1"
                  sx={{ 
                    fontWeight: 800, 
                    color: '#0f172a', 
                    fontSize: { xs: '1.05rem', sm: '1.3rem', md: '1.45rem' },
                    lineHeight: 1.2,
                    letterSpacing: '-0.01em',
                    my: 0.2
                  }}
                >
                  MUNICIPALITY OF CONCEPCION
                </Typography>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#334155', 
                    fontSize: { xs: '0.82rem', sm: '0.95rem' }
                  }}
                >
                  Office of the Municipal Treasurer
                </Typography>
              </Box>

              <Box
                component="img"
                src="/logo-right.png"
                alt="Official Seal of Municipal Treasurer's Office"
                sx={{
                  width: { xs: 48, sm: 60, md: 64 },
                  height: { xs: 48, sm: 60, md: 64 },
                  objectFit: 'contain',
                  display: { xs: 'none', sm: 'block' }
                }}
              />
            </Box>

            {/* Right: Staff Portal Button */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<Security sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/login')}
                sx={{ 
                  color: '#1e293b',
                  borderColor: '#cbd5e1',
                  bgcolor: '#ffffff',
                  fontWeight: 700,
                  borderRadius: 1.5,
                  px: 2,
                  py: 0.75,
                  fontSize: '0.82rem',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: '#94a3b8'
                  }
                }}
              >
                Staff Portal
              </Button>
            </Box>

            {/* Mobile Menu Icon */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton size="medium" edge="end" onClick={handleDrawerToggle} sx={{ color: '#1e293b' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 3. Navigation Bar (Clean Light Style) */}
      <AppBar 
        position="sticky" 
        color="default" 
        elevation={scrolled ? 1 : 0} 
        sx={{ 
          bgcolor: '#ffffff', 
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 42, md: 46 } }}>
            {/* Desktop Navigation Links */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, flexGrow: 1 }}>
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                sx={{ color: '#0f172a', fontWeight: 700, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                Home
              </Button>
              <Button
                onClick={() => navigate('/check-water-bills')}
                startIcon={<WaterDrop sx={{ fontSize: 16, color: '#0284c7' }} />}
                sx={{ color: '#0f172a', fontWeight: 600, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                Water Bills & QRPh
              </Button>
              <Button
                onClick={() => navigate('/check-unclaimed-checks')}
                startIcon={<Receipt sx={{ fontSize: 16, color: '#d97706' }} />}
                sx={{ color: '#0f172a', fontWeight: 600, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                Unclaimed Checks
              </Button>
              <Button
                onClick={() => scrollToSection('services-section')}
                sx={{ color: '#475569', fontWeight: 600, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                E-Services
              </Button>
              <Button
                onClick={() => scrollToSection('charter-section')}
                sx={{ color: '#475569', fontWeight: 600, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                Citizen's Charter
              </Button>
              <Button
                onClick={() => scrollToSection('transparency-section')}
                sx={{ color: '#475569', fontWeight: 600, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                Transparency
              </Button>
              <Button
                onClick={() => scrollToSection('directory-section')}
                sx={{ color: '#475569', fontWeight: 600, px: 1.5, py: 0.6, fontSize: '0.84rem', textTransform: 'none', '&:hover': { bgcolor: '#f1f5f9' } }}
              >
                About & Helpdesk
              </Button>
            </Box>

            {/* Mobile Header Title */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', flexGrow: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#0f172a">
                Official Treasury Portal
              </Typography>
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{ 
                  color: '#1e293b', 
                  borderColor: '#cbd5e1', 
                  fontSize: '0.75rem',
                  py: 0.3,
                  textTransform: 'none'
                }}
              >
                Staff Login
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Box component="nav">
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* 4. Public Advisory Strip (Clean Light Style) */}
      <Box sx={{ bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', py: 1, px: 2 }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Chip
              label="ADVISORY"
              size="small"
              sx={{ bgcolor: '#e2e8f0', color: '#1e293b', fontWeight: 700, fontSize: '0.68rem', height: 20 }}
            />
            <Typography variant="caption" sx={{ color: '#334155', fontWeight: 500, fontSize: '0.82rem', flex: 1 }}>
              Online Water Billing payments are active 24/7 via BSP National QRPh. Cashier Windows at Municipal Hall open Mon-Fri 8:00 AM – 5:00 PM (No Noon Break).
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* 5. Clean, Professional Light Hero Section */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 5, md: 7 }, borderBottom: '1px solid #e2e8f0' }}>
        <Container maxWidth="xl">
          <Box sx={{ maxWidth: 840, mx: 'auto', textAlign: 'center', mb: 5 }}>
            <Chip
              icon={<VerifiedUser sx={{ fontSize: 14, color: '#1e293b !important' }} />}
              label="Official Online Citizen Services & Revenue Administration"
              variant="outlined"
              size="small"
              sx={{ 
                fontWeight: 600, 
                color: '#334155', 
                borderColor: '#cbd5e1', 
                bgcolor: '#f8fafc',
                mb: 2, 
                fontSize: '0.74rem' 
              }}
            />
            <Typography 
              variant="h3" 
              component="h2"
              sx={{ 
                fontWeight: 800, 
                fontSize: { xs: '1.75rem', sm: '2.3rem', md: '2.75rem' },
                color: '#0f172a',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                mb: 1.75
              }}
            >
              Municipal Treasurer's Office
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#475569', 
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: 720,
                mx: 'auto'
              }}
            >
              Inquire water utility bills, settle municipal dues with instant QRPh payment receipts, verify issued disbursement checks, and review official treasury guidelines.
            </Typography>
          </Box>

          {/* Two Primary Action Cards (Clean, Structured) */}
          <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 960, mx: 'auto' }}>
            {/* Card 1: Water Billing */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                variant="outlined"
                onClick={() => navigate('/check-water-bills')}
                sx={{
                  p: 3,
                  borderRadius: 2.5,
                  bgcolor: '#ffffff',
                  borderColor: '#cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  '&:hover': {
                    borderColor: '#0284c7',
                    boxShadow: '0 4px 12px rgba(2, 132, 199, 0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#f0f9ff', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <WaterDrop sx={{ fontSize: 24 }} />
                  </Box>
                  <Chip label="Pay via QRPh" size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#334155' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color="#0f172a" gutterBottom>
                  Water Billing & Online Payment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5, flexGrow: 1, mb: 2 }}>
                  Search your water account balance, check monthly consumption readings, and generate a dynamic QRPh code for GCash, Maya, and bank payments.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#0284c7', fontWeight: 600, fontSize: '0.85rem' }}>
                  <span>Inquire & Pay Water Bill</span>
                  <ArrowForward sx={{ fontSize: 15 }} />
                </Box>
              </Paper>
            </Grid>

            {/* Card 2: Unclaimed Checks */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                variant="outlined"
                onClick={() => navigate('/check-unclaimed-checks')}
                sx={{
                  p: 3,
                  borderRadius: 2.5,
                  bgcolor: '#ffffff',
                  borderColor: '#cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  '&:hover': {
                    borderColor: '#d97706',
                    boxShadow: '0 4px 12px rgba(217, 119, 6, 0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#fffbeb', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Receipt sx={{ fontSize: 24 }} />
                  </Box>
                  <Chip label="Disbursements" size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#334155' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color="#0f172a" gutterBottom>
                  Unclaimed Checks Tracker
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', lineHeight: 1.5, flexGrow: 1, mb: 2 }}>
                  Verify issued municipal disbursement checks for suppliers, contractors, and beneficiaries. Check CAFOA voucher status and claiming guidelines.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#d97706', fontWeight: 600, fontSize: '0.85rem' }}>
                  <span>Track Released Checks</span>
                  <ArrowForward sx={{ fontSize: 15 }} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 6. Citizen E-Services Grid (Clean Uniform Styling) */}
      <Box id="services-section" sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Municipal Treasury Services
            </Typography>
            <Typography variant="h4" component="h2" fontWeight={800} color="#0f172a">
              Citizen Revenue & Assessment Services
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}>
              Official revenue schedules, online utility payments, and procedural requirements.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Service 1: Water Billing */}
            <Grid item xs={12} sm={6} lg={4}>
              <Card 
                elevation={0}
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  p: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#94a3b8' }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <WaterDrop sx={{ color: '#334155', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Water Utility Billing
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5, mb: 2 }}>
                    Monthly consumption ledger, meter readings, Senior Citizen discounts, and dynamic BSP QRPh payment.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 0, pt: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/check-water-bills')}
                    sx={{ color: '#1e293b', borderColor: '#cbd5e1', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    Open Water Billing Portal
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Service 2: Unclaimed Checks */}
            <Grid item xs={12} sm={6} lg={4}>
              <Card 
                elevation={0}
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  p: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#94a3b8' }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Receipt sx={{ color: '#334155', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Unclaimed Checks
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5, mb: 2 }}>
                    Search released checks for suppliers, contractors, and beneficiaries. View required claiming IDs.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 0, pt: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => navigate('/check-unclaimed-checks')}
                    sx={{ color: '#1e293b', borderColor: '#cbd5e1', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    Search Released Checks
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Service 3: Real Property Tax */}
            <Grid item xs={12} sm={6} lg={4}>
              <Card 
                elevation={0}
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  p: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#94a3b8' }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Domain sx={{ color: '#334155', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Real Property Tax (Amilyar)
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5, mb: 2 }}>
                    Land and building assessment guidelines, prompt payment discounts (up to 20%), and Tax Clearances.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 0, pt: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setServiceModal('rpt')}
                    sx={{ color: '#1e293b', borderColor: '#cbd5e1', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    View RPT Guidelines & Rates
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Service 4: Business Permits & Taxes */}
            <Grid item xs={12} sm={6} lg={4}>
              <Card 
                elevation={0}
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  p: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#94a3b8' }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Business sx={{ color: '#334155', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Business Permits & Taxes
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5, mb: 2 }}>
                    Annual business permit renewal (Deadline: Jan 20), Local Business Tax (LBT), and regulatory clearances.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 0, pt: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setServiceModal('bplo')}
                    sx={{ color: '#1e293b', borderColor: '#cbd5e1', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    View Business Tax Info
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Service 5: Civil Registry Fees */}
            <Grid item xs={12} sm={6} lg={4}>
              <Card 
                elevation={0}
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  p: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#94a3b8' }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Badge sx={{ color: '#334155', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Civil Registry Fees
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5, mb: 2 }}>
                    Certified True Copies of Birth, Death, Marriage documents, Marriage License, and combined DST charges.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 0, pt: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setServiceModal('mcr')}
                    sx={{ color: '#1e293b', borderColor: '#cbd5e1', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    View Civil Registry Fees
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {/* Service 6: Community Tax Certificate */}
            <Grid item xs={12} sm={6} lg={4}>
              <Card 
                elevation={0}
                variant="outlined"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  bgcolor: '#ffffff',
                  borderRadius: 2,
                  p: 2.5,
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: '#94a3b8' }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <LocalAtm sx={{ color: '#334155', fontSize: 22 }} />
                    <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                      Cedula (Community Tax)
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5, mb: 2 }}>
                    Individual and Corporate Cedula computation guidelines for employment, legal notarization, and contracts.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 0, pt: 1 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setServiceModal('cedula')}
                    sx={{ color: '#1e293b', borderColor: '#cbd5e1', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    View Cedula Guidelines
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 7. Citizen's Charter & Step-by-Step Guides (Clean Minimalist Design) */}
      <Box id="charter-section" sx={{ py: { xs: 6, md: 8 }, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Citizen's Charter • RA 11032
            </Typography>
            <Typography variant="h4" component="h2" fontWeight={800} color="#0f172a">
              Procedural Step-by-Step Guidelines
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mt: 1 }}>
              Clear and transparent guidelines for transacting with the Municipal Treasurer's Office.
            </Typography>
          </Box>

          <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: '#ffffff' }}>
            <Tabs
              value={charterTab}
              onChange={(e, v) => setCharterTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                bgcolor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                '& .MuiTab-root': { fontWeight: 700, py: 1.75, fontSize: '0.85rem', textTransform: 'none', color: '#64748b' },
                '& .Mui-selected': { color: '#0f172a' }
              }}
            >
              <Tab label="1. Online Water Bill via QRPh" />
              <Tab label="2. Claiming Disbursement Checks" />
              <Tab label="3. Real Property Tax Discounts" />
              <Tab label="4. Cedula Requirements" />
            </Tabs>

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              {/* Tab 1: Water Bill */}
              {charterTab === 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a" gutterBottom>
                    How to Inquire & Settle Water Utility Bills via QRPh:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Follow these three simple steps to check your balance and pay online without queuing at the cashier:
                  </Typography>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc', height: '100%' }}>
                        <Typography variant="caption" fontWeight={700} color="#64748b">STEP 1</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ my: 0.5 }}>
                          Search Account
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
                          Navigate to the <strong>Water Bills</strong> portal. Enter your Account Number, Meter No., or Name to retrieve your latest bill.
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc', height: '100%' }}>
                        <Typography variant="caption" fontWeight={700} color="#64748b">STEP 2</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ my: 0.5 }}>
                          Generate QRPh
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
                          Click "Pay with QRPh". A unique BSP National QR code with your exact bill amount is instantly displayed.
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc', height: '100%' }}>
                        <Typography variant="caption" fontWeight={700} color="#64748b">STEP 3</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" sx={{ my: 0.5 }}>
                          Scan & Confirm
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
                          Open GCash, Maya, ShopeePay, or your bank app. Scan the QR code, authorize payment, and save your digital receipt.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/check-water-bills')}
                      endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      sx={{ bgcolor: '#1e293b', color: '#ffffff', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0f172a' } }}
                    >
                      Inquire Water Bill Now
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Tab 2: Claiming Checks */}
              {charterTab === 1 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a" gutterBottom>
                    Checklist for Claiming Municipal Disbursement Checks:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Please present the following documents at the Municipal Treasury Cashier Window:
                  </Typography>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                          Individual Payees / Personnel:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.6 }}>
                          • One (1) Valid Government-issued Photo ID (PhilID, Driver's License, Passport, PRC, UMID, Voter's ID)<br />
                          • If through Authorized Representative: Signed Authorization Letter + Original Valid IDs of both Payee and Representative<br />
                          • Copy of signed Disbursement Voucher (DV) or CAFOA tracking number
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                          Suppliers & Commercial Contractors:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.6 }}>
                          • BIR Registered Official Receipt (OR) or Official Collection Receipt for the full check amount<br />
                          • Special Power of Attorney (SPA) or Secretary's Certificate for designated liaison officer<br />
                          • Company ID and Valid Government ID of authorized collector
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/check-unclaimed-checks')}
                      endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                      sx={{ bgcolor: '#1e293b', color: '#ffffff', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0f172a' } }}
                    >
                      Search Unclaimed Checks
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Tab 3: RPT Discounts */}
              {charterTab === 2 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a" gutterBottom>
                    Real Property Tax (Amilyar) Payment Schedule:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Prompt payment discount rates under the Local Government Code and Municipal Revenue Code:
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: '#f8fafc', textAlign: 'center' }}>
                        <Typography variant="caption" color="#64748b" fontWeight={700}>ADVANCE ANNUAL</Typography>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" my={0.5}>20% Discount</Typography>
                        <Typography variant="caption" color="text.secondary">Paid on or before Jan 31</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: '#f8fafc', textAlign: 'center' }}>
                        <Typography variant="caption" color="#64748b" fontWeight={700}>PROMPT PAYMENT</Typography>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" my={0.5}>10% Discount</Typography>
                        <Typography variant="caption" color="text.secondary">Paid on or before Mar 31</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: '#f8fafc', textAlign: 'center' }}>
                        <Typography variant="caption" color="#64748b" fontWeight={700}>QUARTERLY DEADLINES</Typography>
                        <Typography variant="body2" fontWeight={700} color="#0f172a" my={0.5}>Q1 to Q4</Typography>
                        <Typography variant="caption" color="text.secondary">Mar 31, Jun 30, Sep 30, Dec 31</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: '#f8fafc', textAlign: 'center' }}>
                        <Typography variant="caption" color="#64748b" fontWeight={700}>LATE SURCHARGE</Typography>
                        <Typography variant="body2" fontWeight={700} color="#b91c1c" my={0.5}>2% per month</Typography>
                        <Typography variant="caption" color="text.secondary">Max 72% (36 months)</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 4: Cedula */}
              {charterTab === 3 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a" gutterBottom>
                    Community Tax Certificate (Cedula) Computation:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Payable on or before the last day of February each calendar year:
                  </Typography>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                          Individual Cedula Formula:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
                          • Basic Tax: <strong>₱5.00</strong> (fixed)<br />
                          • Additional Tax: <strong>₱1.00 for every ₱1,000.00</strong> of gross income, salaries, or real property earnings from the preceding year (maximum ₱5,000.00).
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                          Corporate Cedula Formula:
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
                          • Basic Tax: <strong>₱500.00</strong> (fixed for all registered corporations)<br />
                          • Additional Tax: <strong>₱2.00 for every ₱5,000.00</strong> of gross receipts from business in the Philippines (maximum ₱10,000.00).
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* 8. Transparency, Compliance & Good Governance (Clean Light Box) */}
      <Box id="transparency-section" sx={{ py: { xs: 5, md: 7 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
              Good Governance & Standards
            </Typography>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              Official Compliance & Legal Standards
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#ffffff', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                  Full Disclosure Policy
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.5}>
                  Compliant with DILG guidelines on transparent posting of LGU budgets, collections, and disbursements.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#ffffff', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                  ARTA (RA 11032) Compliant
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.5}>
                  Strict enforcement of Anti-Red Tape service timelines and continuous "No Noon Break" cashier service.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#ffffff', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                  BSP National QRPh
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.5}>
                  Official interoperable digital payment standard supporting GCash, Maya, ShopeePay, and major banks.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#ffffff', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
                  COA Audited Accounts
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.5}>
                  Regular reconciliation and submission of municipal revenue ledgers to the Commission on Audit.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 9. Mandate & Helpdesk Directory (Clean Two-Column Layout) */}
      <Box id="directory-section" sx={{ py: { xs: 6, md: 8 }, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            {/* Left: Mandate */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                Office Mandate
              </Typography>
              <Typography variant="h5" fontWeight={800} color="#0f172a" gutterBottom>
                Office of the Municipal Treasurer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
                Under <strong>Section 470 of Republic Act No. 7160</strong> (Local Government Code of 1991), the Municipal Treasurer's Office is mandated to manage the collection and custody of local revenues, taxes, utility fees, and public disbursements, maintaining fiscal stability and accountability for the Municipality of Concepcion, Romblon.
              </Typography>

              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="#334155" display="block">CORE OBJECTIVES:</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', mt: 0.5 }}>
                  1. Transparent and accountable collection of local government revenues.<br />
                  2. Prompt and secure disbursement of approved municipal funds.<br />
                  3. Continuous modernization of public utilities and digital payment facilities.
                </Typography>
              </Paper>
            </Grid>

            {/* Right: Helpdesk Directory */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle1" fontWeight={800} color="#0f172a" gutterBottom>
                  Office Directory & Public Helpdesk
                </Typography>
                <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

                <Stack spacing={1.75}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <LocationOn sx={{ color: '#64748b', fontSize: 18, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Address:</Typography>
                      <Typography variant="body2" fontWeight={600} color="#0f172a">
                        Ground Floor, Municipal Hall Building, Poblacion, Concepcion, Romblon 5516
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <AccessTime sx={{ color: '#64748b', fontSize: 18, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Office & Cashier Hours:</Typography>
                      <Typography variant="body2" fontWeight={600} color="#0f172a">
                        Monday – Friday: 8:00 AM – 5:00 PM (No Noon Break)
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Email sx={{ color: '#64748b', fontSize: 18, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Official Email:</Typography>
                      <Typography variant="body2" fontWeight={600} color="#0f172a">
                        mtoconcepcion@gmail.com
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Phone sx={{ color: '#64748b', fontSize: 18, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Hotline / Public Assistance:</Typography>
                      <Typography variant="body2" fontWeight={600} color="#0f172a">
                        (042) 123-4567
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 10. Official Philippine Government Footer (Clean Light Gray) */}
      <Box sx={{ bgcolor: '#f1f5f9', color: '#475569', pt: 5, pb: 4, borderTop: '1px solid #e2e8f0' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} sx={{ mb: 3 }}>
            {/* Col 1: LGU Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box component="img" src="/logo-left.png" alt="LGU Seal" sx={{ width: 36, height: 36 }} />
                <Box component="img" src="/logo-right.png" alt="MTO Seal" sx={{ width: 36, height: 36 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                Municipality of Concepcion, Romblon
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                Municipal Treasurer's Office Integrated Public Services & Revenue Management Portal.
              </Typography>
            </Grid>

            {/* Col 2: Services */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="caption" fontWeight={700} color="#0f172a" display="block" gutterBottom>
                E-SERVICES
              </Typography>
              <Stack spacing={0.75} sx={{ fontSize: '0.82rem' }}>
                <Typography component="a" onClick={() => navigate('/check-water-bills')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Water Billing
                </Typography>
                <Typography component="a" onClick={() => navigate('/check-unclaimed-checks')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Unclaimed Checks
                </Typography>
                <Typography component="a" onClick={() => setServiceModal('rpt')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Property Tax (RPT)
                </Typography>
                <Typography component="a" onClick={() => setServiceModal('bplo')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Business Permits
                </Typography>
              </Stack>
            </Grid>

            {/* Col 3: Governance */}
            <Grid item xs={6} sm={3} md={3}>
              <Typography variant="caption" fontWeight={700} color="#0f172a" display="block" gutterBottom>
                GOVERNANCE
              </Typography>
              <Stack spacing={0.75} sx={{ fontSize: '0.82rem' }}>
                <Typography component="a" onClick={() => scrollToSection('charter-section')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Citizen's Charter
                </Typography>
                <Typography component="a" onClick={() => scrollToSection('transparency-section')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Full Disclosure Policy
                </Typography>
                <Typography component="a" onClick={() => navigate('/login')} sx={{ color: '#475569', cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
                  Authorized Staff Login
                </Typography>
              </Stack>
            </Grid>

            {/* Col 4: National Portal Links */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="caption" fontWeight={700} color="#0f172a" display="block" gutterBottom>
                REPUBLIC OF THE PHILIPPINES
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                All official content is in public domain unless otherwise stated.
              </Typography>
              <Stack spacing={0.5} sx={{ fontSize: '0.78rem' }}>
                <Typography sx={{ color: '#64748b' }}>• Bureau of Local Government Finance (BLGF)</Typography>
                <Typography sx={{ color: '#64748b' }}>• Department of Finance (DOF)</Typography>
                <Typography sx={{ color: '#64748b' }}>• Province of Romblon Official Website</Typography>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5, borderColor: '#e2e8f0' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" color="#64748b">
              © {new Date().getFullYear()} LGU Concepcion, Romblon. All Rights Reserved.
            </Typography>
            <Typography variant="caption" color="#64748b">
              Municipal Treasurer's Office • Concepcion, Romblon 5516
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* 11. Informative Modal Dialogs (Clean Light Style) */}
      
      {/* RPT Modal */}
      <Dialog open={serviceModal === 'rpt'} onClose={() => setServiceModal(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: '#f8fafc', color: '#0f172a', fontWeight: 800, borderBottom: '1px solid #e2e8f0' }}>
          Real Property Tax (Amilyar) Guidelines & Schedules
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom sx={{ mt: 1 }}>
            Payment Schedules & Applicable Discounts:
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2.5, borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rate / Discount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Condition</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Advance Annual Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>20% Discount</TableCell>
                  <TableCell>Paid on or before January 31</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Prompt Current Year Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>10% Discount</TableCell>
                  <TableCell>Paid on or before March 31</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Quarterly Installments</TableCell>
                  <TableCell>Base Tax Rate</TableCell>
                  <TableCell>Mar 31, Jun 30, Sep 30, Dec 31</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Delinquency Penalty</TableCell>
                  <TableCell sx={{ color: '#b91c1c', fontWeight: 700 }}>2% per month</TableCell>
                  <TableCell>Maximum 72% surcharge</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
            Requirements for Securing Real Property Tax Clearance:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
            1. Official Receipt of current year RPT payment.<br />
            2. Photocopy of latest Tax Declaration (TD).<br />
            3. Certification Fee: ₱50.00 + Documentary Stamp Tax (DST): ₱30.00.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setServiceModal(null)} variant="outlined" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* BPLO Modal */}
      <Dialog open={serviceModal === 'bplo'} onClose={() => setServiceModal(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: '#f8fafc', color: '#0f172a', fontWeight: 800, borderBottom: '1px solid #e2e8f0' }}>
          Business Permits & Local Business Tax (LBT) Guidelines
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom sx={{ mt: 1 }}>
            Annual Business Permit Renewal Period:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', mb: 2 }}>
            All commercial establishments operating in Concepcion must renew their Mayor's Business Permit on or before <strong>January 20</strong> of each year.
          </Typography>

          <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom>
            Renewal Checklist:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem' }}>
            1. DTI Business Name Certificate / SEC Articles of Incorporation / CDA Registration.<br />
            2. Barangay Business Clearance for current calendar year.<br />
            3. Sworn Statement of Gross Receipts / Previous Year Financial Statements.<br />
            4. Sanitary Permit & Health Certificates from Rural Health Unit (RHU).<br />
            5. Fire Safety Inspection Certificate (FSIC) from BFP.<br />
            6. Real Property Tax (Amilyar) Clearance of business premises.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setServiceModal(null)} variant="outlined" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* MCR Modal */}
      <Dialog open={serviceModal === 'mcr'} onClose={() => setServiceModal(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: '#f8fafc', color: '#0f172a', fontWeight: 800, borderBottom: '1px solid #e2e8f0' }}>
          Municipal Civil Registry Schedule of Fees
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom sx={{ mt: 1 }}>
            Standard Registry Fees & Documentary Stamp Tax:
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ my: 1.5, borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Service / Certificate</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Basic Fee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">DST</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Certified True Copy (Birth, Death, Marriage)</TableCell>
                  <TableCell align="right">₱100.00</TableCell>
                  <TableCell align="right">₱30.00</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>₱130.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Marriage License Application Fee</TableCell>
                  <TableCell align="right">₱150.00</TableCell>
                  <TableCell align="right">₱30.00</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>₱180.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Late Registration Processing Fee</TableCell>
                  <TableCell align="right">₱150.00</TableCell>
                  <TableCell align="right">₱30.00</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>₱180.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Certificate of No Record / Negative</TableCell>
                  <TableCell align="right">₱100.00</TableCell>
                  <TableCell align="right">₱30.00</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>₱130.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary">
            Note: All civil registry certifications include BIR Documentary Stamp Tax (DST) of ₱30.00 per copy.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setServiceModal(null)} variant="outlined" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cedula Modal */}
      <Dialog open={serviceModal === 'cedula'} onClose={() => setServiceModal(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: '#f8fafc', color: '#0f172a', fontWeight: 800, borderBottom: '1px solid #e2e8f0' }}>
          Community Tax Certificate (Cedula) Computation Guide
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#0f172a" gutterBottom sx={{ mt: 1 }}>
            How Community Tax is Assessed:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', mb: 2, borderRadius: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} color="#0f172a">Individual Cedula:</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', mt: 0.5 }}>
              • Basic Tax: <strong>₱5.00</strong> (fixed)<br />
              • Additional Tax: <strong>₱1.00 for every ₱1,000.00</strong> of gross salary, business income, or property revenue (maximum ₱5,000.00).
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} color="#0f172a">Corporate Cedula:</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', mt: 0.5 }}>
              • Basic Tax: <strong>₱500.00</strong> (fixed)<br />
              • Additional Tax: <strong>₱2.00 for every ₱5,000.00</strong> of gross Philippine receipts (maximum ₱10,000.00).
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setServiceModal(null)} variant="outlined" sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 12. Mobile Bottom Bar Navigation */}
      <MobileBottomBar
        items={[
          { name: 'Home', icon: <AccountBalance />, path: '/' },
          { name: 'Water Bills', icon: <WaterDrop />, path: '/check-water-bills' },
          { name: 'Unclaimed Checks', icon: <Receipt />, path: '/check-unclaimed-checks' },
          { name: 'Staff Login', icon: <Login />, path: '/login' }
        ]}
        currentPath="/"
        onNavigate={(path) => navigate(path)}
        onToggleMenu={handleDrawerToggle}
        isMenuOpen={mobileOpen}
      />
    </Box>
  );
};

export default Homepage;
